import { Component, DestroyRef, effect, inject } from '@angular/core';
import { Validators, ValidatorFn, ValidationErrors, AbstractControl, FormBuilder } from '@angular/forms';
import { faAngleRight, faL } from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, switchMap, forkJoin, map, startWith, of, catchError, tap } from 'rxjs';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { Extension } from 'src/app/models/centrex/extension';
import { SdaService } from 'src/app/services/centrex/sda.service';
import { Sda } from 'src/app/models/centrex/sda';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { HttpClient } from '@angular/common/http';
import { ToolsService } from 'src/app/services/centrex/tools.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatList, MatListItem } from '@angular/material/list';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-instance',
    templateUrl: './instance.component.html',
    styleUrls: ['./instance.component.scss'],
    imports: [
        FaIconComponent,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInputModule,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatOption,
        MatCardHeader,
        MatCardTitle,
        MatError,
        MatList,
        MatListItem,
        MatSlideToggle,
        MatButton,
        AsyncPipe,
        TranslateModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
    ]
})
export class InstanceComponent {
    private readonly instanceService = inject(InstanceService);
    private readonly router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private formBuilder = inject(FormBuilder);

    instanceForm: FormGroup;
    instanceInformations: Instance;

    // Icône
    public faAngleRight = faAngleRight;

    numberExtensions: Observable<Extension[]>;
    numberExtensionsList: Extension[] = [];
    voicemailExtensions: Observable<Extension[]>;
    voicemailExtensionsList: Extension[] = [];
    sdas: Observable<Sda[]>;
    sdasList: Sda[] = [];

    extensionsUrlIsValid: boolean = null;
    provisionningUrlIsValid: boolean = null;
    voiceUrlIsValid: boolean = null;
    smsUrlIsValid: boolean = null;
    audioUrlIsValid: boolean = null;
    mailUrlIsValid: boolean = null;

    constructor(
        protected http: HttpClient,
        protected usersService: UsersService,
        private extensionService: ExtensionService,
        private organizationsService: OrganizationsService,
        private instanceSda: SdaService,
        private spinner: NgxSpinnerService,
        private toolsService: ToolsService
    ) {
        this.instanceForm = this.formBuilder.group({
            display: [null, Validators.required],
            domain: [null, Validators.required],
            proxy: [null, Validators.required],
            phone: [null, [this.autocompletePhoneValidator()]],
            receptionist: [null, [this.autocompleteReceptionistValidator()]],
            voicemail: [null, [this.autocompleteVoicemailValidator()]],
            extensionsUrl: [null, Validators.required],
            provisionningUrl: [null, Validators.required],
            voiceUrl: [null, Validators.required],
            smsUrl: [null, Validators.required],
            audioUrl: [null, Validators.required],
            mailUrl: [null, Validators.required],
            feature_VM: [null],
            feature_VM_MAIL: [null],
            feature_VM_SMS: [null],
            feature_VM_TRANSCRIPT: [null],
        });

        this.instanceForm.updateValueAndValidity();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
            } else {
                this.instanceForm.reset();
            }
        });

        this.featureSelectionChange();
    }

    isInitialized: boolean = false;

    loadDatas() {
        this.spinner.show();

        forkJoin([this.extensionService.getExtensions(), this.instanceSda.getSdas(), this.instanceService.getInstance()])
            .pipe(
                switchMap(([extensions, sdas, instance]) => {
                    if (instance === null) {
                        return forkJoin([of(extensions), of(sdas), this.instanceService.getDefaultInstance()]);
                    } else {
                        return forkJoin([of(extensions), of(sdas), of(instance)]);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: ([extensions, sdas, instance]) => {
                    this.numberExtensionsList = extensions.filter((ext) => ext.type === 'user' || ext.type === 'group');
                    this.voicemailExtensionsList = extensions.filter((ext) => ext.type === 'service' && ext.service === 'VOICEMAIL');
                    this.sdasList = sdas;

                    this.numberExtensions = this.instanceForm.get('receptionist').valueChanges.pipe(
                        startWith<string | Extension>(''),
                        map((value) => {
                            const name = typeof value === 'string' ? value : null;
                            return name ? this.receptionistSearch(name as string) : this.numberExtensionsList.slice();
                        })
                    );

                    this.sdas = this.instanceForm.get('phone').valueChanges.pipe(
                        startWith<string | Sda>(''),
                        map((value) => {
                            const name = typeof value === 'string' ? value : null;
                            return name ? this.phoneSearch(name as string) : this.sdasList.slice();
                        })
                    );

                    this.voicemailExtensions = this.instanceForm.get('voicemail').valueChanges.pipe(
                        startWith<string | Extension>(''),
                        map((value) => {
                            const name = typeof value === 'string' ? value : null;
                            return name ? this.voicemailSearch(name as string) : this.voicemailExtensionsList.slice();
                        })
                    );

                    this.isInitialized = instance != null;
                    this.instanceInformations = instance;

                    if (instance != null) {
                        this.initValues(instance);
                        this.spinner.hide();
                    } else {
                        this.instanceService
                            .getDefaultInstance()
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe({
                                next: (defaultInstance) => {
                                    this.initValues(defaultInstance);
                                    this.spinner.hide();
                                },
                                error: (error) => {
                                    this.spinner.hide();
                                },
                            });
                    }
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    initValues(instance: Instance) {
        this.instanceForm.controls['display'].setValue(instance?.display);
        this.instanceForm.controls['domain'].setValue(instance?.domain);
        this.instanceForm.controls['proxy'].setValue(instance?.proxy);
        this.instanceForm.controls['phone'].setValue(instance?.phone);
        this.instanceForm.controls['receptionist'].setValue(instance?.receptionist);
        this.instanceForm.controls['voicemail'].setValue(instance?.voicemail);
        this.instanceForm.controls['extensionsUrl'].setValue(instance?.extensionsUrl);
        this.instanceForm.controls['provisionningUrl'].setValue(instance?.provisionningUrl);
        this.instanceForm.controls['voiceUrl'].setValue(instance?.voiceUrl);
        this.instanceForm.controls['smsUrl'].setValue(instance?.smsUrl);
        this.instanceForm.controls['audioUrl'].setValue(instance?.audioUrl);
        this.instanceForm.controls['mailUrl'].setValue(instance?.mailUrl);
        ////this.instanceForm.controls['features'].setValue(Object.keys(instance?.features).map((key, idx) => {
        ////	if (instance?.features[key] && instance?.features[key] != '')
        ////		return key + '=' + instance?.features[key];
        ////	else
        ////		return key;
        ////}).join('\r\n'));
        if (instance?.features) {
            this.instanceForm.controls['feature_VM'].setValue(Object.keys(instance?.features).includes('VM'));
            this.instanceForm.controls['feature_VM_MAIL'].setValue(Object.keys(instance?.features).includes('VM_MAIL'));
            this.instanceForm.controls['feature_VM_SMS'].setValue(Object.keys(instance?.features).includes('VM_SMS'));
            this.instanceForm.controls['feature_VM_TRANSCRIPT'].setValue(Object.keys(instance?.features).includes('VM_TRANSCRIPT'));
        }

        for (var i in this.instanceForm.controls) {
            this.instanceForm.controls[i].markAsTouched();
        }
    }

    /**
     * Vérifie l'URL d'une API
     * @param apiName Nom de l'API.
     */
    checkUrl(apiName: string) {
        let url: string = '';
        let control: AbstractControl = null;

        this.spinner.show();

        switch (apiName) {
            case 'extensions':
                control = this.instanceForm.get('extensionsUrl');
                url = control.value + '/v2/centrex/extensions';
                break;
            case 'provisionning':
                control = this.instanceForm.get('provisionningUrl');
                url = control.value + '/v2/centrex/devices';
                break;
            case 'voice':
                control = this.instanceForm.get('voiceUrl');
                url = control.value + '/v2/call/voice';
                break;
            case 'audio':
                control = this.instanceForm.get('audioUrl');
                url = control.value + '/v2/audio/tts';
                break;
            case 'sms':
                control = this.instanceForm.get('smsUrl');
                url = control.value + '/v2/call/sms';
                break;
            case 'mail':
                control = this.instanceForm.get('mailUrl');
                url = control.value + '/v2/call/mail';
                break;
        }

        this.toolsService
            .checkApi(apiName, url)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (x) => {
                    control.setErrors(null);

                    this.setApiUrlIsValid(apiName, true);

                    this.spinner.hide();
                },
                error: (err) => {
                    control.setErrors({ valid: false, statusText: err.error?.detail ?? err.message });

                    this.setApiUrlIsValid(apiName, false);

                    this.spinner.hide();
                },
            });
    }

    /**
     * Vérifie l'URL d'une API.
     * @param apiName Nom de l'API.
     * @returns Retourne un Observable avec le nom de l'API traitée.
     */
    checkUrlObservable(apiName: string): Observable<any> {
        let url: string = '';
        let control: AbstractControl = null;

        this.spinner.show();

        switch (apiName) {
            case 'extensions':
                control = this.instanceForm.get('extensionsUrl');
                url = control.value + '/v2/centrex/extensions';
                break;
            case 'provisionning':
                control = this.instanceForm.get('provisionningUrl');
                url = control.value + '/v2/centrex/devices';
                break;
            case 'voice':
                control = this.instanceForm.get('voiceUrl');
                url = control.value + '/v2/call/voice';
                break;
            case 'audio':
                control = this.instanceForm.get('audioUrl');
                url = control.value + '/v2/audio/tts';
                break;
            case 'sms':
                control = this.instanceForm.get('smsUrl');
                url = control.value + '/v2/call/sms';
                break;
            case 'mail':
                control = this.instanceForm.get('mailUrl');
                url = control.value + '/v2/call/mail';
                break;
        }

        return this.toolsService.checkApi(apiName, url).pipe(
            map(() => {
                return { apiName: apiName };
            })
        );
    }

    /**
     * Evènement lors du changement de la valeur de l'URL d'une API.
     * @param apiName Nom de l'API.
     */
    urlChange(apiName: string) {
        this.setApiUrlIsValid(apiName, null);
    }

    /**
     * Affiche la validité ou non de l'API.
     * @param apiName Nom de l'API.
     * @param value Valeur indiquant si l'URL de l'API est valide ou non.
     */
    private setApiUrlIsValid(apiName: string, value: boolean) {
        switch (apiName) {
            case 'extensions':
                this.extensionsUrlIsValid = value;
                break;
            case 'provisionning':
                this.provisionningUrlIsValid = value;
                break;
            case 'voice':
                this.voiceUrlIsValid = value;
                break;
            case 'audio':
                this.audioUrlIsValid = value;
                break;
            case 'sms':
                this.smsUrlIsValid = value;
                break;
            case 'mail':
                this.mailUrlIsValid = value;
                break;
        }
    }

    /**
     * Affecte l'erreur au Control correspondant à l'API.
     * @param apiName Nom de l'API.
     * @param error Erreur
     */
    setErrorControl(apiName: string, error: ValidationErrors) {
        switch (apiName) {
            case 'extensions':
                this.instanceForm.get('extensionsUrl').setErrors(error);
                break;
            case 'provisionning':
                this.instanceForm.get('provisionningUrl').setErrors(error);
                break;
            case 'voice':
                this.instanceForm.get('voiceUrl').setErrors(error);
                break;
            case 'audio':
                this.instanceForm.get('audioUrl').setErrors(error);
                break;
            case 'sms':
                this.instanceForm.get('smsUrl').setErrors(error);
                break;
            case 'mail':
                this.instanceForm.get('mailUrl').setErrors(error);
                break;
        }
    }

    onSubmit() {
        this.spinner.show();

        forkJoin([
            this.checkUrlObservable('extensions').pipe(catchError((e) => of({ apiName: 'extensions', error: e }))),
            this.checkUrlObservable('provisionning').pipe(catchError((e) => of({ apiName: 'provisionning', error: e }))),
            this.checkUrlObservable('voice').pipe(catchError((e) => of({ apiName: 'voice', error: e }))),
            this.checkUrlObservable('audio').pipe(catchError((e) => of({ apiName: 'audio', error: e }))),
            this.checkUrlObservable('sms').pipe(catchError((e) => of({ apiName: 'sms', error: e }))),
            this.checkUrlObservable('mail').pipe(catchError((e) => of({ apiName: 'mail', error: e }))),
        ]).subscribe({
            next: (a) => {
                let hasError: boolean = false;
                a.forEach((result) => {
                    if (result.error) {
                        this.setErrorControl(result.apiName, { valid: false, statusText: result.error.error?.detail ?? result.error.message });
                        this.setApiUrlIsValid(result.apiName, false);
                        hasError = true;
                    } else {
                        this.setErrorControl(result.apiName, null);
                        this.setApiUrlIsValid(result.apiName, true);
                    }
                });

                if (hasError === false) {
                    this.instanceService
                        .getInstance()
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (instance) => {
                                if (instance == null) {
                                    instance = new Instance();
                                    instance.id = this.organizationsService.getCurrentOrganization()?.id ?? '';
                                }

                                console.log(this.instanceForm.controls);

                                instance.display = this.instanceForm.controls['display'].value;
                                instance.domain = this.instanceForm.controls['domain'].value;
                                instance.proxy = this.instanceForm.controls['proxy'].value;
                                instance.phone = this.instanceForm.controls['phone'].value;
                                instance.receptionist = this.instanceForm.controls['receptionist'].value;
                                instance.voicemail = this.instanceForm.controls['voicemail'].value;
                                instance.extensionsUrl = this.instanceForm.controls['extensionsUrl'].value;
                                instance.provisionningUrl = this.instanceForm.controls['provisionningUrl'].value;
                                instance.voiceUrl = this.instanceForm.controls['voiceUrl'].value;
                                instance.smsUrl = this.instanceForm.controls['smsUrl'].value;
                                instance.audioUrl = this.instanceForm.controls['audioUrl'].value;
                                instance.mailUrl = this.instanceForm.controls['mailUrl'].value;
                                instance.features = new Object();
                                if (this.instanceForm.controls['feature_VM'].value) instance.features['VM'] = '';
                                if (this.instanceForm.controls['feature_VM_MAIL'].value) instance.features['VM_MAIL'] = '';
                                if (this.instanceForm.controls['feature_VM_SMS'].value) instance.features['VM_SMS'] = '';
                                if (this.instanceForm.controls['feature_VM_TRANSCRIPT'].value) instance.features['VM_TRANSCRIPT'] = '';
                                //this.instanceForm.controls['features'].value.split('\n').map((value, idx) => {
                                //	var values = value.replace('\r', '').split('=');
                                //	console.log(values);
                                //	if (values.length == 2)
                                //		instance.features[values[0]] = values[1];
                                //	else if (values.length == 1)
                                //		instance.features[values[0]] = '';
                                //});
                                console.log(instance.features);

                                this.instanceService
                                    .initializeInstance(instance)
                                    .pipe(takeUntilDestroyed(this.destroyRef))
                                    .subscribe({
                                        next: () => {
                                            this.isInitialized = true;

                                            this.spinner.hide();
                                        },
                                        error: (error) => {
                                            this.spinner.hide();
                                        },
                                    });
                            },
                            error: (error) => {
                                this.spinner.hide();
                            },
                        });
                } else {
                    this.spinner.hide();
                }
            },
            error: (err) => {
                this.spinner.hide();
            },
        });
    }

    phoneChange(event: MatAutocompleteSelectedEvent) {
        let phone = event.option.value as Sda;
        if (phone) {
            this.instanceForm.get('phone').setValue(phone);
            // this.selectedExtension = extension;
        }
    }

    private phoneSearch(value: string): Sda[] {
        const filterValue = value.toLowerCase();

        return this.sdasList.filter((a) => a.number.toLowerCase().includes(filterValue));
    }

    private autocompletePhoneValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.sdasList.findIndex((a) => a.number === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }

    private autocompleteReceptionistValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.numberExtensionsList.findIndex((a) => a.number === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }

    receptionistChange(event: MatAutocompleteSelectedEvent) {
        let receptionistNumber = event.option.value;
        if (receptionistNumber) {
            this.instanceForm.get('receptionist').setValue(receptionistNumber);
            // this.selectedExtension = extension;
        }
    }

    private receptionistSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.numberExtensionsList.filter((a) => a.number.toLowerCase().includes(filterValue));
    }

    displayReceptionist = (receptionistNumber: string): string => {
        const receptionistExtension = this.numberExtensionsList?.find((a) => a.number === receptionistNumber);
        return receptionistNumber ? `${receptionistExtension.number} (${receptionistExtension.name})` : '';
    };

    voicemailChange(event: MatAutocompleteSelectedEvent) {
        let voicemail = event.option.value as Extension;
        if (voicemail) {
            this.instanceForm.get('voicemail').setValue(voicemail);
            // this.selectedExtension = extension;
        }
    }

    featureSelectionChange() {
        if (!this.instanceForm.get('feature_VM').value) {
            this.instanceForm.get('feature_VM_MAIL')?.disable();
            this.instanceForm.get('feature_VM_SMS')?.disable();
        } else {
            this.instanceForm.get('feature_VM_MAIL')?.enable();
            this.instanceForm.get('feature_VM_SMS')?.enable();
        }

        if (!this.instanceForm.get('feature_VM').value || (!this.instanceForm.get('feature_VM_MAIL').value && !this.instanceForm.get('feature_VM_SMS').value)) {
            this.instanceForm.get('feature_VM_TRANSCRIPT')?.disable();
        } else {
            this.instanceForm.get('feature_VM_TRANSCRIPT')?.enable();
        }
    }

    private voicemailSearch(value: string): Extension[] {
        const filterValue = value.toLowerCase();

        return this.voicemailExtensionsList.filter((a) => a.number.toLowerCase().includes(filterValue));
    }

    private autocompleteVoicemailValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (control.value && this.voicemailExtensionsList.findIndex((a) => a.number === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
